import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, Injector, OnInit, forwardRef } from '@angular/core';
import { ControlContainer, FormGroupDirective, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { UserGeneralTabFields } from '@nimbus-global/features/permissions/models/user/user-general-tab-fields.model';
import { BulkValue } from '@nimbus-global/features/bulk-edit/models/bulk-value';
import { Feature } from '@nimbus-global/features/feature-flipping/feature.enum';
import { FeatureFlippingService } from '@nimbus-global/features/feature-flipping/feature-flipping.service';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { INimbusFormControl, INimbusFormGroup } from '@nimbus-library/features/shared/angular/forms';
import { IInputComponent } from '@nimbus-library/features/shared/components/material/i-input-component';
import { ILicenseItem, LicenseType } from '@nimbus-library/features/shared/components/material/input-license/input-license.model';
import {
  MODALITY_TASK_LIMIT_CONTROLS,
  TASK_LIMIT_MAX,
  TASK_LIMIT_MIN,
} from '@nimbus-library/features/shared/components/material/input-license/task-parallelization.model';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { UserEditViewModel } from '@nimbus-global/features/user/models/domain/edit/user-edit.view-model';

@UntilDestroy()
@Component({
  selector: 'nimbus-user-input-license-checkbox-group',
  templateUrl: './user-input-license-checkbox-group.component.html',
  styleUrls: ['./user-input-license-checkbox-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => UserInputLicenseCheckboxGroupComponent),
    },
  ],
})
export class UserInputLicenseCheckboxGroupComponent extends IInputComponent<LicenseType[]> implements OnInit {
  @Input() public isBulk: boolean;
  @Input() public selectedUsersCount: number = 1;
  @Input() public originalValue: UserEditViewModel;
  @Input() public taskLimitBulkValues: Record<string, BulkValue<number>> = {};
  @Input() public taskLimitPlaceholders: Record<string, string> = {};
  @Input() public isTaskParallelizationIdentical: boolean = true;
  @Input() public tenantId: string;

  private innerOptions: BulkValue<ILicenseItem>[] = [];

  public readonly ICONS = ICONS;
  public readonly LicenseType = LicenseType;
  public readonly TranslationKeys = TranslationKeys;
  public readonly taskLimitMin = TASK_LIMIT_MIN;
  public readonly taskLimitMax = TASK_LIMIT_MAX;
  public readonly UserGeneralTabFields = UserGeneralTabFields;

  public taskLimitIdentical: Record<string, boolean> = {};

  public modalities$ = new BehaviorSubject<Partial<Record<LicenseType, BulkValue<ILicenseItem>>>>({});
  public modalityTaskLimitMax$: Observable<number>;
  public isInstantMessagingAllowed$: Observable<boolean>;
  public isEmailAllowed$: Observable<boolean>;
  public isCompanionAllowed$: Observable<boolean>;
  public isDirectCallHandlingAllowed$: Observable<boolean>;

  @Input()
  public get options(): BulkValue<ILicenseItem>[] {
    return this.innerOptions;
  }

  public set options(value: BulkValue<ILicenseItem>[]) {
    this.modalities$.next({
      [LicenseType.AudioVideo]: value.find((item) => item.value.licenseType === LicenseType.AudioVideo),
      [LicenseType.InstantMessaging]: value.find((item) => item.value.licenseType === LicenseType.InstantMessaging),
      [LicenseType.ExternalTask]: value.find((item) => item.value.licenseType === LicenseType.ExternalTask),
      [LicenseType.Email]: value.find((item) => item.value.licenseType === LicenseType.Email),
      [LicenseType.Companion]: value.find((item) => item.value.licenseType === LicenseType.Companion),
      [LicenseType.DirectCallHandling]: value.find((item) => item.value.licenseType === LicenseType.DirectCallHandling),
    });

    this.innerOptions = value;
  }

  public constructor(
    public readonly changeDetectionRef: ChangeDetectorRef,
    public readonly injector: Injector,
    public readonly translateService: TranslateService,
    private readonly featureFlippingService: FeatureFlippingService,
  ) {
    super(changeDetectionRef, injector, translateService);
  }

  public ngOnInit(): void {
    this.isInstantMessagingAllowed$ = this.featureFlippingService.areEnabled$([Feature.InstantMessaging]).pipe(
      distinctUntilChanged(isEqual),
      map((features) => features.InstantMessaging),
    );
    this.isEmailAllowed$ = this.featureFlippingService.areEnabled$([Feature.EmailRouting]).pipe(
      distinctUntilChanged(isEqual),
      map((features) => features.EmailRouting),
    );
    this.isCompanionAllowed$ = this.featureFlippingService.areEnabled$([Feature.Companion]).pipe(
      distinctUntilChanged(isEqual),
      map((features) => features.Companion),
    );
    // DirectCallHandling is a feature-management-only flag, so it is resolved for the tenant being
    // administered rather than through the old-style areEnabled$ map. Resolving it for the signed-in
    // user's own tenant hid the licence whenever that differed from the tenant on screen.
    this.isDirectCallHandlingAllowed$ = this.featureFlippingService
      .isEnabledInFeatureManagementByTenantId$(this.tenantId, Feature.DirectCallHandling)
      .pipe(distinctUntilChanged(isEqual));

    this.modalityTaskLimitMax$ = this.form.controls.totalTaskLimit.valueChanges.pipe(
      startWith(this.form.controls.totalTaskLimit.value),
      map((v) => (v && v >= TASK_LIMIT_MIN ? Math.min(v, TASK_LIMIT_MAX) : TASK_LIMIT_MAX)),
      distinctUntilChanged(),
    );

    this.observeTaskLimitControls();
    this.observeTaskLimitBulkState();
  }

  public selectionChange(value: LicenseType): void {
    const tempValue: LicenseType[] = this.value ? [...this.value] : [];
    const index = tempValue.findIndex((item) => item === value);

    if (index >= 0) {
      tempValue.splice(index, 1);
    } else {
      tempValue.push(value);
    }

    this.value = tempValue.length ? tempValue : null;

    (this.options || []).forEach((option: BulkValue<ILicenseItem>) => {
      const isActive = (this.value || []).findIndex((item) => item === option.value.licenseType) >= 0;

      if (option.value.isActive !== isActive) {
        option.value.isActive = isActive;
        option.identical = true;
        option.dirty = true;
        if (this.isBulk) {
          option.aggregatedCount = isActive ? this.selectedUsersCount : 0;
        }
      }
    });

    this.changeDetectionRef.detectChanges();
  }

  public get isTaskParallelizationEnabled(): boolean {
    const control = this.form.controls.isTaskParallelizationEnabled;
    if (this.isBulk && !this.isTaskParallelizationIdentical && !control.dirty) {
      return false;
    }
    return !!control.value;
  }

  protected get form(): INimbusFormGroup<UserEditViewModel> {
    return this.injector.get(FormGroupDirective).control as INimbusFormGroup<UserEditViewModel>;
  }

  private get activeLicenses(): LicenseType[] {
    return this.form.controls.licenses.value || [];
  }

  private observeTaskLimitBulkState(): void {
    if (!this.isBulk) {
      return;
    }

    MODALITY_TASK_LIMIT_CONTROLS.forEach(({ controlName }) => {
      const control = this.form.get(controlName);
      if (control) {
        this.taskLimitIdentical[controlName] = this.taskLimitBulkValues[controlName]?.identical ?? true;
        control.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
          const bulkIdentical = this.taskLimitBulkValues[controlName]?.identical;
          this.taskLimitIdentical[controlName] = bulkIdentical === true || (control.dirty && value != null);
          this.changeDetectionRef.markForCheck();
        });
      }
    });
  }

  private observeTaskLimitControls(): void {
    combineLatest([
      this.form.controls.isTaskParallelizationEnabled.valueChanges.pipe(
        startWith(this.form.controls.isTaskParallelizationEnabled.value),
        distinctUntilChanged(),
        map(Boolean),
      ),
      this.form.controls.licenses.valueChanges.pipe(
        startWith(this.form.controls.licenses.value),
        map((licenses) => licenses || []),
      ),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([isTaskParallelizationEnabled, licenses]) => {
        this.updateTaskLimitControls(isTaskParallelizationEnabled, licenses);
        this.changeDetectionRef.markForCheck();
      });

    this.form.controls.totalTaskLimit.valueChanges.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe(() => {
      MODALITY_TASK_LIMIT_CONTROLS.forEach(({ controlName, licenseType }) => {
        if (this.isTaskLimitVisible(this.isTaskParallelizationEnabled, this.activeLicenses, licenseType)) {
          this.updateTaskLimitControl(controlName, true);
          const control = this.form.get(controlName);
          if (control?.invalid) {
            control.markAsTouched({ onlySelf: true });
          }
        }
      });
      this.changeDetectionRef.markForCheck();
    });
  }

  private updateTaskLimitControls(isTaskParallelizationEnabled: boolean, licenses: LicenseType[]): void {
    this.updateTaskLimitControl('totalTaskLimit', this.isTaskLimitVisible(isTaskParallelizationEnabled, licenses));

    MODALITY_TASK_LIMIT_CONTROLS.forEach(({ controlName, licenseType }) => {
      this.updateTaskLimitControl(controlName, this.isTaskLimitVisible(isTaskParallelizationEnabled, licenses, licenseType));
    });
  }

  private isTaskLimitVisible(isTaskParallelizationEnabled: boolean, licenses: LicenseType[], licenseType?: LicenseType): boolean {
    return !!isTaskParallelizationEnabled && (!licenseType || licenses.includes(licenseType));
  }

  private updateTaskLimitControl(controlName: string, isVisible: boolean): void {
    const control = this.form.get(controlName) as INimbusFormControl;
    const isModalityControl = controlName !== 'totalTaskLimit';
    const totalTaskLimit = this.form.controls.totalTaskLimit.value;
    const maxValue =
      isModalityControl && totalTaskLimit && totalTaskLimit >= TASK_LIMIT_MIN ? Math.min(totalTaskLimit, TASK_LIMIT_MAX) : TASK_LIMIT_MAX;

    if (!this.isBulk) {
      control.setValidators(isVisible ? [Validators.required, Validators.min(TASK_LIMIT_MIN), Validators.max(maxValue)] : []);
    }

    if (!isVisible) {
      control.setValue(TASK_LIMIT_MIN, { emitEvent: false });
      control.markAsPristine({ onlySelf: true });
      control.markAsUntouched({ onlySelf: true });
    }

    control.updateValueAndValidity({ emitEvent: false });
  }
}
